import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Download, Filter, Edit3, History as HistoryIcon, X } from "lucide-react";
import MovementFixDialog from "@/components/MovementFixDialog";
import ManageRequestersDialog from "@/components/ManageRequestersDialog";
import { DepartmentBadge } from "@/components/DepartmentChips";
import { fmtQty } from "@/lib/pack";

const TABS = [
  { key: "all", label: "All", style: "nb-btn-dark" },
  { key: "OUT", label: "Taken out", style: "nb-btn-primary" },
  { key: "IN", label: "Received", style: "nb-btn-success" },
  { key: "stocktake", label: "Stocktake", style: "nb-btn-primary" },
  { key: "baseline", label: "Baseline", style: "nb-btn-primary" },
];

const money = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "—"
    : `$${Number(n).toFixed(2)}`;

const History = () => {
  const { isManager } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = ["OUT", "IN", "all"].includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "all";
  const [tab, setTab] = useState(initialTab);
  const [movs, setMovs] = useState([]);
  const [search, setSearch] = useState("");
  const [fixMovement, setFixMovement] = useState(null); // movement being edited
  const [showDeleted, setShowDeleted] = useState(false);
  // Date range — ISO YYYY-MM-DD; empty = no bound. Presets below fill these.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // When a requester chip is tapped, `requesterFilter` locks the visible
  // rows to that name (case-insensitive) — independent of the free-text
  // search box so the user can still type an additional filter on top.
  const [requesterFilter, setRequesterFilter] = useState("");
  const [manageRequestersOpen, setManageRequestersOpen] = useState(false);

  const load = async () => {
    // "stocktake" and "baseline" are virtual tabs: they don't filter the
    // API `action` param (baseline rows can be IN or OUT). Instead we fetch
    // all and filter client-side. Everything else maps 1:1 to the backend.
    const params = ["all", "stocktake", "baseline"].includes(tab)
      ? {}
      : { action: tab };
    if (showDeleted) params.include_deleted = true;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    try {
      const { data } = await api.get("/movements", { params: { ...params, limit: 2000 } });
      setMovs(data);
    } catch {
      toast.error("Could not load history");
    }
  };

  useEffect(() => {
    load();
  }, [tab, showDeleted, dateFrom, dateTo]);

  // Date presets — click "7d" and both dateFrom/dateTo lock to that window.
  const setPreset = (days) => {
    if (days === 0) {
      setDateFrom("");
      setDateTo("");
      return;
    }
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - days + 1);
    const iso = (d) => d.toISOString().slice(0, 10);
    setDateFrom(iso(from));
    setDateTo(iso(today));
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rf = requesterFilter.trim().toLowerCase();
    return movs.filter((m) => {
      // Virtual tabs — filter on the row's stocktake/is_baseline flags.
      if (tab === "stocktake" && !m.stocktake) return false;
      if (tab === "baseline" && !m.is_baseline) return false;
      // Locked requester filter takes precedence — matches case-insensitively.
      if (rf && (m.requester || "").toLowerCase() !== rf) return false;
      if (!q) return true;
      return (
        m.stock_name.toLowerCase().includes(q) ||
        m.user_name.toLowerCase().includes(q) ||
        (m.supplier || "").toLowerCase().includes(q) ||
        (m.batch_ref || "").toLowerCase().includes(q) ||
        (m.requester || "").toLowerCase().includes(q)
      );
    });
  }, [movs, search, requesterFilter, tab]);

  // "By requester" rollup — case-insensitive grouping so "sarah" and
  // "Sarah" collapse into a single chip. The displayed name is the most
  // common casing across the underlying rows so it looks natural.
  const byRequester = useMemo(() => {
    const agg = {};
    for (const m of filtered) {
      const raw = (m.requester || "").trim();
      if (!raw || m.deleted || m.action !== "OUT") continue;
      const key = raw.toLowerCase();
      const row = agg[key] || {
        key,
        casings: {},
        count: 0,
        spend: 0,
        items: new Set(),
      };
      row.casings[raw] = (row.casings[raw] || 0) + 1;
      row.count += 1;
      const cost = m.unit_cost != null ? Number(m.unit_cost) : 0;
      row.spend += cost * Number(m.qty || 0);
      row.items.add(m.stock_name);
      agg[key] = row;
    }
    return Object.values(agg)
      .map((r) => ({
        name: Object.entries(r.casings).sort((a, b) => b[1] - a[1])[0][0],
        key: r.key,
        count: r.count,
        item_count: r.items.size,
        spend: Number(r.spend.toFixed(2)),
      }))
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  const csvUrl = () => {
    const token = getToken();
    const params = new URLSearchParams({ auth: token });
    if (tab !== "all") params.set("action", tab);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    return `${API}/movements/export?${params.toString()}`;
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="history-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label-eyebrow">Audit trail · who · when · what</div>
          <h1 className="font-display text-5xl leading-none mt-2">History</h1>
        </div>
        <a
          data-testid="export-csv"
          href={csvUrl()}
          className="nb-btn nb-btn-primary px-5 py-3 flex items-center gap-2 no-underline text-black"
        >
          <Download size={18} strokeWidth={2.5} /> Export CSV
        </a>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`history-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`nb-btn px-4 py-2.5 ${
                tab === t.key ? t.style : "bg-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[200px] relative">
          <Filter
            size={18}
            strokeWidth={2.5}
            className="absolute left-3 top-1/2 -translate-y-1/2"
          />
          <input
            data-testid="history-search-input"
            className="nb-input pl-11 h-12"
            placeholder="Filter by item, user, supplier…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {isManager && (
          <label
            className="nb-btn bg-white h-12 px-3 flex items-center gap-2 text-sm cursor-pointer"
            title="Show soft-deleted movements (excluded from all reports)"
          >
            <input
              data-testid="history-show-deleted-toggle"
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className="accent-[#8A1F1F]"
            />
            Show deleted
          </label>
        )}
      </div>

      {/* Date range row — presets on the left, explicit From/To on the right. */}
      <div
        className="flex flex-wrap items-center gap-2 mb-4"
        data-testid="history-date-range"
      >
        <span className="label-eyebrow text-[#607087] mr-1">Date range</span>
        {[
          { label: "Today", days: 1 },
          { label: "7 d", days: 7 },
          { label: "30 d", days: 30 },
          { label: "90 d", days: 90 },
        ].map((p) => {
          const active = (() => {
            if (!dateFrom || !dateTo) return false;
            const today = new Date().toISOString().slice(0, 10);
            if (dateTo !== today) return false;
            const from = new Date(today);
            from.setDate(from.getDate() - p.days + 1);
            return dateFrom === from.toISOString().slice(0, 10);
          })();
          return (
            <button
              key={p.label}
              type="button"
              data-testid={`history-preset-${p.days}d`}
              onClick={() => setPreset(p.days)}
              className={`nb-btn px-3 py-1.5 text-xs ${
                active ? "nb-btn-primary" : "bg-white"
              }`}
            >
              {p.label}
            </button>
          );
        })}
        <button
          type="button"
          data-testid="history-preset-all"
          onClick={() => setPreset(0)}
          className={`nb-btn px-3 py-1.5 text-xs ${
            !dateFrom && !dateTo ? "nb-btn-primary" : "bg-white"
          }`}
        >
          All
        </button>
        <div className="flex items-center gap-1 ml-1 text-xs">
          <span className="label-eyebrow text-[#607087]">From</span>
          <input
            data-testid="history-date-from"
            type="date"
            className="nb-input h-9 text-xs px-2 w-[10rem]"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <span className="label-eyebrow text-[#607087] ml-1">To</span>
          <input
            data-testid="history-date-to"
            type="date"
            className="nb-input h-9 text-xs px-2 w-[10rem]"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        {(dateFrom || dateTo) && (
          <div className="label-eyebrow text-[#5B3A99] ml-2">
            {filtered.length} row{filtered.length === 1 ? "" : "s"} in range
          </div>
        )}
      </div>

      <div className="nb-card overflow-hidden">
        {byRequester.length > 0 && (
          <div
            className="p-4 border-b-2 border-black bg-[#F1E7FA]"
            data-testid="history-by-requester"
          >
            <div className="label-eyebrow mb-2 flex items-center gap-2 flex-wrap">
              <span>By requester ({byRequester.length}) · reception sign-out totals</span>
              {isManager && (
                <button
                  type="button"
                  data-testid="history-manage-requesters"
                  onClick={() => setManageRequestersOpen(true)}
                  className="nb-btn bg-white px-2.5 py-1 text-xs"
                  title="Rename or merge requesters"
                >
                  Merge / rename…
                </button>
              )}
              {requesterFilter && (
                <button
                  type="button"
                  data-testid="history-requester-clear"
                  onClick={() => setRequesterFilter("")}
                  className="nb-btn nb-btn-primary px-3 py-1 text-xs flex items-center gap-1"
                  title="Show all requesters again"
                >
                  <X size={12} strokeWidth={3} /> Show all — clear “{requesterFilter}”
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {byRequester.slice(0, 12).map((r) => {
                const active = requesterFilter.toLowerCase() === r.key;
                return (
                  <button
                    key={r.key}
                    type="button"
                    data-testid={`history-requester-chip-${r.name}`}
                    onClick={() =>
                      setRequesterFilter(active ? "" : r.name)
                    }
                    className={`nb-btn px-2.5 py-1 text-xs flex items-center gap-2 ${
                      active ? "nb-btn-primary" : "bg-white"
                    }`}
                    title={active ? "Tap again to show all" : `Show only ${r.name}`}
                  >
                    <b>{r.name}</b>
                    <span className={active ? "opacity-90" : "text-[#607087]"}>
                      {r.count}× · {r.item_count} item{r.item_count === 1 ? "" : "s"}
                      {r.spend > 0 && <> · ${r.spend.toFixed(2)}</>}
                    </span>
                  </button>
                );
              })}
              {byRequester.length > 12 && (
                <span className="text-xs text-[#607087] italic self-center">
                  +{byRequester.length - 12} more
                </span>
              )}
            </div>
          </div>
        )}
        <div className="grid grid-cols-12 gap-2 p-3 border-b border-white/40 bg-gradient-to-r from-[#0D1F3C]/95 to-[#1E3C66]/85 text-white label-eyebrow rounded-t-[22px]">
          <div className="col-span-2">When</div>
          <div className="col-span-1">Action</div>
          <div className="col-span-2">Item</div>
          <div className="col-span-1">Qty</div>
          <div className="col-span-1">Unit cost / Line</div>
          <div className="col-span-2">User · Dept</div>
          <div className="col-span-1">Supplier</div>
          <div className="col-span-1">Batch</div>
          <div className="col-span-1">Notes</div>
        </div>
        <div
          className="max-h-[560px] overflow-y-auto scrollbar-clean"
          data-testid="history-list"
        >
          {filtered.map((m) => {
            const line =
              m.unit_cost === null || m.unit_cost === undefined
                ? null
                : Number(m.unit_cost) * Number(m.qty || 0);
            return (
              <div
                key={m.id}
                className={`grid grid-cols-12 gap-2 p-3 border-b border-black/10 items-center hover:bg-[#DFEEF7]/40 ${
                  m.deleted ? "bg-[#FCE4E4]/40 opacity-60 line-through" : ""
                }`}
                data-testid={`movement-${m.id}`}
              >
                <div className="col-span-2 font-mono-num text-xs">{fmt(m.created_at)}</div>
                <div className="col-span-1 flex flex-wrap items-center gap-1">
                  <span
                    className="font-display text-xs px-2 py-0.5 border-2 border-black rounded"
                    style={{
                      background: m.action === "OUT" ? "#228EBF" : "#5FBE7E",
                      color: "#fff",
                    }}
                  >
                    {m.action}
                  </span>
                  {m.deleted && (
                    <span
                      data-testid={`movement-deleted-badge-${m.id}`}
                      className="text-[10px] px-1.5 py-0.5 border-2 border-[#8A1F1F] rounded bg-[#FCE4E4] text-[#8A1F1F] no-underline"
                      title={`Deleted by ${m.deleted_by || "—"}${m.deleted_at ? " on " + new Date(m.deleted_at).toLocaleDateString() : ""}`}
                    >
                      deleted
                    </span>
                  )}
                  {m.audit_log?.length > 0 && !m.deleted && (
                    <span
                      data-testid={`movement-edited-badge-${m.id}`}
                      className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 border-2 border-black rounded bg-[#F1E7FA] text-[#5B3A99]"
                      title={`Edited ${m.audit_log.length}× — most recently by ${m.audit_log[m.audit_log.length - 1].user_name || "—"}`}
                    >
                      <HistoryIcon size={9} strokeWidth={2.5} />
                      {m.audit_log.length}
                    </span>
                  )}
                  {m.opening && (
                    <span
                      data-testid={`movement-opening-badge-${m.id}`}
                      className="text-[10px] px-1.5 py-0.5 border-2 border-[#7A4A00] rounded bg-[#FFE9C2] text-[#7A4A00] font-bold"
                      title="Opening-balance / pre-launch reconciliation — excluded from day-to-day reports"
                    >
                      opening
                    </span>
                  )}
                  {m.stocktake && (
                    <span
                      data-testid={`movement-stocktake-badge-${m.id}`}
                      className="text-[10px] px-1.5 py-0.5 border-2 border-[#144F82] rounded bg-[#DDEEFA] text-[#144F82] font-bold"
                      title="Stocktake reconciliation — excluded from day-to-day reports"
                    >
                      stocktake
                    </span>
                  )}
                  {m.is_baseline && (
                    <span
                      data-testid={`movement-baseline-badge-${m.id}`}
                      className="text-[10px] px-1.5 py-0.5 border-2 border-[#7A2E00] rounded bg-[#FFE0B3] text-[#7A2E00] font-bold uppercase tracking-wider"
                      title="Baseline reset — snapped current_qty to physical count. Excluded from Shrinkage and Spend reports."
                    >
                      Baseline
                    </span>
                  )}
                </div>
                <div className="col-span-2 font-display truncate">{m.stock_name}</div>
                <div className="col-span-1 font-mono-num font-bold text-sm">
                  {m.sub_qty && m.sub_unit ? (
                    <>
                      {fmtQty(m.sub_qty)} {m.sub_unit}
                      <div className="text-[9px] text-[#607087] font-normal">
                        ({fmtQty(m.qty)} {m.unit})
                      </div>
                    </>
                  ) : (
                    <>
                      {fmtQty(m.qty)} {m.unit}
                    </>
                  )}
                </div>
                <div className="col-span-1 font-mono-num text-xs">
                  <div className="flex items-center gap-1.5">
                    <div>
                      <div>{money(m.unit_cost)}/u</div>
                      {line !== null && (
                        <div className="text-[10px] text-[#607087]">
                          = {money(line)}
                        </div>
                      )}
                    </div>
                    {isManager && (
                      <button
                        data-testid={`movement-fix-open-${m.id}`}
                        onClick={() => setFixMovement(m)}
                        className="nb-btn bg-white p-1 shrink-0"
                        aria-label="fix movement"
                        title="Fix historical qty or unit cost"
                      >
                        <Edit3 size={10} strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-span-2 text-xs">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="truncate">{m.user_name}</span>
                    {m.department && (
                      <span data-testid={`movement-dept-${m.id}`}>
                        <DepartmentBadge department={m.department} />
                      </span>
                    )}
                  </div>
                  {m.requester && (
                    <div
                      data-testid={`movement-requester-${m.id}`}
                      className="text-[10px] text-[#5B3A99] truncate mt-0.5"
                      title={`Signed out to ${m.requester}`}
                    >
                      → {m.requester}
                    </div>
                  )}
                </div>
                <div className="col-span-1 text-xs text-[#6B6B6B] truncate" title={m.supplier || ""}>
                  {m.supplier || ""}
                </div>
                <div
                  className="col-span-1 text-xs text-[#6B6B6B] truncate"
                  data-testid={`movement-batch-${m.id}`}
                  title={m.batch_ref || ""}
                >
                  {m.batch_ref || ""}
                </div>
                <div
                  className="col-span-1 text-xs text-[#6B6B6B] truncate"
                  data-testid={`movement-notes-${m.id}`}
                  title={m.notes || ""}
                >
                  {m.notes || ""}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="p-10 text-center text-[#6B6B6B] text-sm">
              No movements yet.
            </div>
          )}
        </div>
      </div>

      <MovementFixDialog
        open={!!fixMovement}
        movement={fixMovement}
        onClose={() => setFixMovement(null)}
        onSaved={(updated) => {
          setMovs((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
        }}
      />

      <ManageRequestersDialog
        open={manageRequestersOpen}
        onClose={() => setManageRequestersOpen(false)}
        onChanged={() => load()}
      />
    </div>
  );
};

export default History;
